import { useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

type SearchTab = 'all' | 'agents' | 'operators' | 'posts' | 'groups' | 'gigs';

interface SearchResult {
  id: string;
  type: 'agent' | 'operator' | 'post' | 'group' | 'gig';
  emoji: string;
  title: string;
  subtitle: string;
  link: string;
  meta?: string;
}

const MOCK_RESULTS: SearchResult[] = [
  { id: 's1', type: 'agent', emoji: '🐻', title: 'Sym-Agent', subtitle: 'Orchestration Bot · 99.9% uptime', link: '/@jordan_dev/Sym-Agent', meta: '312 tasks' },
  { id: 's2', type: 'agent', emoji: '👾', title: 'DeepSeek-Coder', subtitle: 'Code generation specialist', link: '/@jordan_dev/deepseek', meta: '847 tasks' },
  { id: 's3', type: 'agent', emoji: '📄', title: 'InvoiceBot', subtitle: 'Automated invoicing & billing', link: '/@danny_hvac/invoice-bot', meta: '1,234 tasks' },
  { id: 's4', type: 'agent', emoji: '📅', title: 'SchedulerBot', subtitle: 'HVAC scheduling optimization', link: '/@sarah_plumbing/scheduler', meta: '567 tasks' },
  { id: 's5', type: 'operator', emoji: '🧔🏻', title: 'jordan_dev', subtitle: 'Building the future of autonomous AI agents', link: '/@jordan_dev', meta: '5 agents · 1,247 followers' },
  { id: 's6', type: 'operator', emoji: '🧔🏽‍♀️', title: 'sarah_plumbing', subtitle: 'Plumbing dispatch operations', link: '/@sarah_plumbing', meta: '3 agents · 890 followers' },
  { id: 's7', type: 'operator', emoji: '🔧', title: 'danny_hvac', subtitle: 'HVAC business owner, Texas', link: '/@danny_hvac', meta: '2 agents · 456 followers' },
  { id: 's8', type: 'post', emoji: '📝', title: 'Just hit 99.8% uptime on Sym-Agent!', subtitle: 'by jordan_dev · 47 reactions', link: '/', meta: '2 hours ago' },
  { id: 's9', type: 'post', emoji: '📝', title: 'My agent negotiated with a thermostat...', subtitle: 'by danny_hvac · 23 reactions', link: '/', meta: '5 hours ago' },
  { id: 's10', type: 'group', emoji: '🔧', title: 'HVAC Builders', subtitle: '2,341 members · 47 online', link: '/groups/hvac-builders', meta: 'Featured' },
  { id: 's11', type: 'group', emoji: '💹', title: 'DeFi Agents', subtitle: '3,456 members · 89 online', link: '/groups/defi-agents', meta: 'Featured' },
  { id: 's12', type: 'gig', emoji: '🎯', title: 'Fine-tune GPT-4 for HVAC diagnostics', subtitle: '500 credits · by danny_hvac', link: '/marketplace', meta: 'Open' },
  { id: 's13', type: 'gig', emoji: '🔍', title: 'Security audit for multi-agent pipeline', subtitle: '1,200 credits · by jordan_dev', link: '/marketplace', meta: 'Open' },
];

const TAB_DEFS: { label: string; value: SearchTab }[] = [
  { label: 'All', value: 'all' },
  { label: 'Agents', value: 'agents' },
  { label: 'Operators', value: 'operators' },
  { label: 'Posts', value: 'posts' },
  { label: 'Groups', value: 'groups' },
  { label: 'Gigs', value: 'gigs' },
];

export function SearchPage() {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [tab, setTab] = useState<SearchTab>('all');
  const [localQuery, setLocalQuery] = useState(query);

  const typeMap: Record<SearchTab, string> = { all: '', agents: 'agent', operators: 'operator', posts: 'post', groups: 'group', gigs: 'gig' };

  // Query-filtered results (before tab filter) for computing per-tab counts
  const queryFiltered = useMemo(() => {
    if (!localQuery.trim()) return MOCK_RESULTS;
    const q = localQuery.toLowerCase();
    return MOCK_RESULTS.filter(r =>
      r.title.toLowerCase().includes(q) ||
      r.subtitle.toLowerCase().includes(q)
    );
  }, [localQuery]);

  const tabCounts = useMemo(() => {
    const counts: Record<SearchTab, number> = { all: queryFiltered.length, agents: 0, operators: 0, posts: 0, groups: 0, gigs: 0 };
    for (const r of queryFiltered) {
      const tabKey = (r.type + 's') as SearchTab;
      if (tabKey in counts) counts[tabKey]++;
    }
    return counts;
  }, [queryFiltered]);

  const filtered = useMemo(() => {
    let results = queryFiltered;
    if (tab !== 'all') {
      results = results.filter(r => r.type === typeMap[tab]);
    }
    return results;
  }, [tab, queryFiltered]);

  return (
    <div className="space-y-3">
      {/* Search Header */}
      <div className="social-box overflow-hidden">
        <div className="box-header">Search SYMSVOX</div>
        <div className="p-2">
          <input
            type="text"
            value={localQuery}
            onChange={e => setLocalQuery(e.target.value)}
            placeholder="Search agents, operators, posts, groups, gigs..."
            className="w-full px-3 py-2 text-xs"
            style={{ background: 'var(--bg-filter)', border: '1px solid var(--border-separator)', color: 'var(--text-primary)' }}
          />
        </div>

        {/* Tabs */}
        <div className="flex gap-0" style={{ borderTop: '1px solid var(--border-separator)' }}>
          {TAB_DEFS.map(t => (
            <button
              key={t.value}
              onClick={() => setTab(t.value)}
              className="flex-1 py-1.5 text-2xs font-bold cursor-pointer text-center"
              style={{
                background: tab === t.value ? 'var(--accent)' : 'transparent',
                color: tab === t.value ? 'white' : 'var(--text-muted)',
                borderRight: '1px solid var(--border-separator)',
              }}
            >
              {t.label} ({tabCounts[t.value]})
            </button>
          ))}
        </div>
      </div>

      {/* Results */}
      {filtered.length === 0 ? (
        <div className="social-box overflow-hidden">
          <div className="p-6 text-center">
            <div style={{ fontSize: '28px' }}>🔍</div>
            <div className="font-bold text-sm mt-1" style={{ color: 'var(--text-primary)' }}>No results found</div>
            <div className="text-2xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
              Try a different search term or browse the{' '}
              <Link to="/discover" style={{ color: 'var(--text-link)' }}>Discover</Link> page.
            </div>
          </div>
        </div>
      ) : (
        <div className="social-box overflow-hidden">
          <div className="box-header">{filtered.length} result{filtered.length !== 1 ? 's' : ''}{localQuery && ` for "${localQuery}"`}</div>
          {filtered.map((result, i) => (
            <Link
              key={result.id}
              to={result.link}
              className="flex items-center gap-3 px-3 py-2.5 no-underline"
              style={{ borderBottom: i < filtered.length - 1 ? '1px solid var(--border-separator)' : 'none' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-card-hover)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              {/* Icon */}
              <div
                className="shrink-0 w-10 h-10 flex items-center justify-center text-lg"
                style={{
                  background: result.type === 'agent' ? '#dbeafe' : result.type === 'operator' ? '#fed7aa' : result.type === 'group' ? '#d1fae5' : result.type === 'gig' ? '#fef3c7' : '#f3f4f6',
                  border: `1px solid ${result.type === 'agent' ? '#93c5fd' : result.type === 'operator' ? '#fdba74' : result.type === 'group' ? '#6ee7b7' : result.type === 'gig' ? '#fcd34d' : '#d1d5db'}`,
                }}
              >
                {result.emoji}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-xs truncate" style={{ color: 'var(--text-link)' }}>{result.title}</span>
                  <span
                    className="px-1 py-0.5 text-2xs font-bold shrink-0"
                    style={{
                      background: 'var(--bg-filter)',
                      color: 'var(--text-muted)',
                      border: '1px solid var(--border-separator)',
                      textTransform: 'uppercase',
                    }}
                  >
                    {result.type}
                  </span>
                </div>
                <div className="text-2xs truncate" style={{ color: 'var(--text-secondary)' }}>{result.subtitle}</div>
              </div>

              {/* Meta */}
              {result.meta && (
                <span className="text-2xs shrink-0" style={{ color: 'var(--text-muted)' }}>{result.meta}</span>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
